import { useState, useEffect } from "react";

import { ChevronDown, ChevronRight, Sparkles, Star, Search, Clock, TrendingUp, Lightbulb } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface Framework {
  id: string;
  name: string;
  description: string;
}

interface Category {
  name: string;
  frameworks: Framework[];
}

interface Bucket {
  name: string;
  description: string;
  categories: Category[];
}

interface Recommendation {
  framework_id: string;
  reason: string;
}

interface SessionFrameworkWidgetProps {
  onSelectFramework: (frameworkId: string) => void;
  sessionId: string;
  recentMessages: Array<{ role: string; content: string }>;
  disabled?: boolean;
}

const FRAMEWORK_BUCKETS: Bucket[] = [
  {
    name: "Everyday Use Cases",
    description: "Save spoons for what matters most",
    categories: [
      {
        name: "Regain Focus & Manage Energy",
        frameworks: [
          { id: "task_prioritisation", name: "Task Prioritisation", description: "Turn chaos into an actionable plan" },
          { id: "deconstructing_big_project", name: "Deconstructing a Big Project", description: "Break down overwhelming projects" },
          { id: "low_energy_week", name: "Designing a Low-Energy Week", description: "Define the minimum to keep moving" },
          { id: "what_to_delegate", name: "Deciding What to Delegate", description: "Overcome delegation paralysis" },
          { id: "blank_page", name: "Overcoming the Blank Page", description: "Turn writer's block into editing" },
          { id: "focus_environment", name: "Designing a Focus-Friendly Environment", description: "Eliminate distractions" },
          { id: "not_to_do_list", name: "Creating a Not-To-Do List", description: "Reclaim focus by stopping" },
        ],
      },
      {
        name: "Communicate with Confidence",
        frameworks: [
          { id: "email_tone_check", name: "Email Tone Check", description: "Ensure emails land right" },
          { id: "difficult_conversation", name: "Difficult Conversation Prep", description: "Role-play tough talks" },
          { id: "elevator_pitch", name: "Refining Your Elevator Pitch", description: "Translate jargon to value" },
          { id: "customer_feedback", name: "Responding to Customer Feedback", description: "Turn feedback into asset" },
          { id: "stakeholder_update", name: "Drafting a Stakeholder Update", description: "Brain-dump to clear update" },
          { id: "networking_event", name: "Preparing for a Networking Event", description: "Reduce networking anxiety" },
          { id: "ambiguous_communication", name: "Decoding Ambiguous Communication", description: "Translate confusing feedback" },
        ],
      },
      {
        name: "Sharpen Strategy & Reduce Risk",
        frameworks: [
          { id: "idea_sanity_check", name: "Quick Idea Sanity Check", description: "Rapid go/no-go analysis" },
        ],
      },
    ],
  },
  {
    name: "Advanced Frameworks",
    description: "Sophisticated strategic thinking",
    categories: [
      {
        name: "Sharpen Strategy & Reduce Risk",
        frameworks: [
          { id: "pre_mortem", name: "Pre-Mortem", description: "Identify risks before they happen" },
          { id: "scenario_planning", name: "Scenario Planning", description: "Prepare for uncertain futures" },
          { id: "swot_analysis", name: "SWOT Analysis", description: "Analyze strengths and threats" },
          { id: "tows_analysis", name: "TOWS Analysis", description: "Turn SWOT into action" },
          { id: "jobs_to_be_done", name: "Jobs to be Done", description: "Understand customer needs" },
          { id: "blue_ocean_strategy", name: "Blue Ocean Strategy", description: "Create uncontested markets" },
          { id: "decision_matrix", name: "Decision Matrix", description: "Structured decision-making" },
        ],
      },
      {
        name: "Regain Focus & Manage Energy",
        frameworks: [
          { id: "why_ladder", name: "The Why Ladder", description: "Connect to deep motivation" },
          { id: "fear_setting", name: "Fear Setting", description: "Deconstruct fears into action" },
          { id: "pillars_fulfillment", name: "Pillars of Fulfillment", description: "Prevent burnout holistically" },
        ],
      },
      {
        name: "Build Better Systems",
        frameworks: [
          { id: "blameless_postmortem", name: "Blameless Post-Mortem", description: "Learn from failures" },
          { id: "seven_whys", name: "Seven Whys (RCA)", description: "Find true root causes" },
        ],
      },
      {
        name: "Communicate with Confidence",
        frameworks: [
          { id: "stakeholder_analysis", name: "Stakeholder Analysis", description: "Map the human landscape" },
        ],
      },
    ],
  },
];

export const SessionFrameworkWidget = ({ onSelectFramework, sessionId, recentMessages, disabled }: SessionFrameworkWidgetProps) => {
  const { user } = useAuth();
  const { execute: invoke } = useEdgeFunction({ auth: true });
  const [expandedBuckets, setExpandedBuckets] = useState<Set<string>>(new Set(["Everyday Use Cases"]));
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [recentlyUsed, setRecentlyUsed] = useState<string[]>([]);
  const [trending, setTrending] = useState<string[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);

  useEffect(() => {
    if (user) {
      fetchFavorites();
      fetchRecentlyUsed();
      fetchTrending();
    }
  }, [user]);

  useEffect(() => {
    if (user && recentMessages.length >= 3) {
      fetchRecommendations();
    }
  }, [user, recentMessages]);

  const fetchFavorites = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from("framework_favorites")
      .select("framework_id")
      .eq("user_id", user.id);

    if (error) {
      console.error("Error fetching favorites:", error);
      return;
    }

    setFavorites(new Set(data?.map(f => f.framework_id) || []));
  };

  const fetchRecentlyUsed = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from("framework_usage")
      .select("framework_id")
      .eq("user_id", user.id)
      .order("used_at", { ascending: false })
      .limit(5);

    if (error) {
      console.error("Error fetching recently used:", error);
      return;
    }

    // Get unique framework IDs
    const uniqueIds = [...new Set(data?.map(f => f.framework_id) || [])].slice(0, 5);
    setRecentlyUsed(uniqueIds);
  };

  const fetchTrending = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from("framework_usage_analytics")
      .select("framework_id, uses_last_7_days")
      .order("uses_last_7_days", { ascending: false })
      .limit(5);

    if (error) {
      console.error("Error fetching trending:", error);
      return;
    }

    setTrending(data?.map(f => f.framework_id) || []);
  };

  const fetchRecommendations = async () => {
    if (!user || loadingRecommendations) return;
    
    setLoadingRecommendations(true);
    try {
      const { data, error } = await invoke('recommend-frameworks', {
        body: { 
          sessionId,
          recentMessages: recentMessages.slice(-5)
        }
      });

      if (error) {
        console.error("Error fetching recommendations:", error);
        return;
      }

      setRecommendations(data?.recommendations || []);
    } catch (error) {
      console.error("Error fetching recommendations:", error);
    } finally {
      setLoadingRecommendations(false);
    }
  };

  const toggleFavorite = async (frameworkId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;

    const isFavorite = favorites.has(frameworkId);

    if (isFavorite) {
      const { error } = await supabase
        .from("framework_favorites")
        .delete()
        .eq("user_id", user.id)
        .eq("framework_id", frameworkId);

      if (error) {
        console.error("Error removing favorite:", error);
        toast.error("Failed to remove favorite");
        return;
      }

      setFavorites(prev => {
        const next = new Set(prev);
        next.delete(frameworkId);
        return next;
      });
      toast.success("Removed from favorites");
    } else {
      const { error } = await supabase
        .from("framework_favorites")
        .insert({ user_id: user.id, framework_id: frameworkId });

      if (error) {
        console.error("Error adding favorite:", error);
        toast.error("Failed to add favorite");
        return;
      }

      setFavorites(prev => new Set([...prev, frameworkId]));
      toast.success("Added to favorites");
    }
  };

  const handleSelectFramework = async (frameworkId: string) => {
    if (!user) return;

    // Track usage
    await supabase
      .from("framework_usage")
      .insert({
        user_id: user.id,
        framework_id: frameworkId,
        session_id: sessionId
      });

    // Refresh recently used
    await fetchRecentlyUsed();

    // Call parent handler
    onSelectFramework(frameworkId);
  };

  const toggleBucket = (bucketName: string) => {
    setExpandedBuckets(prev => {
      const next = new Set(prev);
      if (next.has(bucketName)) {
        next.delete(bucketName);
      } else {
        next.add(bucketName);
      }
      return next;
    });
  };

  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryKey)) {
        next.delete(categoryKey);
      } else {
        next.add(categoryKey);
      }
      return next;
    });
  };

  // Get all frameworks as flat list for search and favorites
  const allFrameworks = FRAMEWORK_BUCKETS.flatMap(bucket =>
    bucket.categories.flatMap(category =>
      category.frameworks.map(f => ({
        ...f,
        bucketName: bucket.name,
        categoryName: category.name
      }))
    )
  );

  // Filter frameworks based on search
  const filteredFrameworks = searchQuery
    ? allFrameworks.filter(f =>
        f.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allFrameworks;

  // Get favorite frameworks
  const favoriteFrameworks = allFrameworks.filter(f => favorites.has(f.id));

  // Get recently used frameworks
  const recentFrameworks = recentlyUsed
    .map(id => allFrameworks.find(f => f.id === id))
    .filter(Boolean) as typeof allFrameworks;

  // Get trending frameworks
  const trendingFrameworks = trending
    .map(id => allFrameworks.find(f => f.id === id))
    .filter(Boolean) as typeof allFrameworks;

  // Get recommended frameworks with reasons
  const recommendedFrameworks = recommendations
    .map(rec => {
      const framework = allFrameworks.find(f => f.id === rec.framework_id);
      return framework ? { ...framework, reason: rec.reason } : null;
    })
    .filter(Boolean) as Array<typeof allFrameworks[0] & { reason: string }>;

  const renderFrameworkButton = (framework: typeof allFrameworks[0], reason?: string, badge?: string) => (
    <div key={framework.id} className="flex gap-1 items-center">
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={(e) => toggleFavorite(framework.id, e)}
        disabled={disabled}
      >
        <Star
          className={cn(
            "h-4 w-4",
            favorites.has(framework.id) && "fill-yellow-400 text-yellow-400"
          )}
        />
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => handleSelectFramework(framework.id)}
        className={cn(
          "flex-1 justify-start text-left h-auto py-2 px-3",
          "hover:bg-primary/10 hover:border-primary"
        )}
      >
        <div className="flex flex-col items-start gap-0.5 w-full">
          <div className="flex items-center justify-between w-full">
            <span className="font-medium text-sm">{framework.name}</span>
            {badge && (
              <Badge variant="secondary" className="ml-2 text-xs">
                {badge}
              </Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground font-normal">
            {reason || framework.description}
          </span>
        </div>
      </Button>
    </div>
  );

  return (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Session Frameworks</CardTitle>
        </div>
        <CardDescription>
          Choose a framework to structure your thinking
        </CardDescription>
        <div className="mt-3">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search frameworks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {searchQuery ? (
          // Search results
          <div className="space-y-1">
            <h4 className="text-sm font-medium mb-2">Search Results ({filteredFrameworks.length})</h4>
            {filteredFrameworks.length === 0 ? (
              <p className="text-sm text-muted-foreground">No frameworks found</p>
            ) : (
              filteredFrameworks.map(f => renderFrameworkButton(f))
            )}
          </div>
        ) : (
          <>
            {/* AI Recommendations Section */}
            {recommendedFrameworks.length > 0 && (
              <div className="space-y-2 pb-2 border-b">
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-primary" />
                  <h4 className="text-sm font-semibold">Recommended for You</h4>
                  {loadingRecommendations && (
                    <span className="text-xs text-muted-foreground">(updating...)</span>
                  )}
                </div>
                <div className="space-y-1">
                  {recommendedFrameworks.map(f => renderFrameworkButton(f, f.reason))}
                </div>
              </div>
            )}

            {/* Favorites Section */}
            {favoriteFrameworks.length > 0 && (
              <div className="space-y-2 pb-2 border-b">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  <h4 className="text-sm font-semibold">Favorites</h4>
                </div>
                <div className="space-y-1">
                  {favoriteFrameworks.map(f => renderFrameworkButton(f))}
                </div>
              </div>
            )}

            {/* Trending Section */}
            {trendingFrameworks.length > 0 && (
              <div className="space-y-2 pb-2 border-b">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  <h4 className="text-sm font-semibold">Trending</h4>
                </div>
                <div className="space-y-1">
                  {trendingFrameworks.map(f => renderFrameworkButton(f, undefined, '🔥'))}
                </div>
              </div>
            )}

            {/* Recently Used Section */}
            {recentFrameworks.length > 0 && (
              <div className="space-y-2 pb-2 border-b">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-semibold">Recently Used</h4>
                </div>
                <div className="space-y-1">
                  {recentFrameworks.map(f => renderFrameworkButton(f))}
                </div>
              </div>
            )}

            {/* All Frameworks by Bucket */}
            {FRAMEWORK_BUCKETS.map((bucket) => (
          <Collapsible
            key={bucket.name}
            open={expandedBuckets.has(bucket.name)}
            onOpenChange={() => toggleBucket(bucket.name)}
          >
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between hover:bg-muted/50"
              >
                <div className="flex flex-col items-start gap-1">
                  <span className="font-semibold">{bucket.name}</span>
                  <span className="text-xs text-muted-foreground">{bucket.description}</span>
                </div>
                {expandedBuckets.has(bucket.name) ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pl-4 space-y-2 mt-2">
              {bucket.categories.map((category) => {
                const categoryKey = `${bucket.name}-${category.name}`;
                return (
                  <Collapsible
                    key={categoryKey}
                    open={expandedCategories.has(categoryKey)}
                    onOpenChange={() => toggleCategory(categoryKey)}
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-between text-sm hover:bg-muted/30"
                      >
                        <span className="font-medium">{category.name}</span>
                        {expandedCategories.has(categoryKey) ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-4 space-y-1 mt-1">
                      {category.frameworks.map((framework) =>
                        renderFrameworkButton({
                          ...framework,
                          bucketName: bucket.name,
                          categoryName: category.name
                        })
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </CollapsibleContent>
          </Collapsible>
        ))}
          </>
        )}
      </CardContent>
    </Card>
  );
};
